import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "item", "number", "status"]
  static values = { url: String, revision: String, editable: Boolean }

  connect() {
    this.busy = false
    this.renumber()
  }

  disconnect() {
    this.drag = null
  }

  ids() { return this.itemTargets.map(item => Number(item.dataset.bookingId)) }

  renumber() {
    this.itemTargets.forEach((item, index) => {
      item.querySelector('[data-team-order-target="number"]').textContent = String(index + 1)
    })
  }

  begin(event) {
    if (this.busy || !this.editableValue || event.button !== 0) return
    event.preventDefault()
    const item = event.currentTarget.closest('[data-team-order-target="item"]')
    this.drag = { item, before: this.ids(), pointerId: event.pointerId }
    event.currentTarget.setPointerCapture(event.pointerId)
    item.classList.add("ring-2", "ring-indigo-500")
  }

  move(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    event.preventDefault()
    const over = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-team-order-target="item"]')
    if (over && over !== this.drag.item && this.listTarget.contains(over)) {
      const bounds = over.getBoundingClientRect()
      this.listTarget.insertBefore(this.drag.item, event.clientY < bounds.top + bounds.height / 2 ? over : over.nextSibling)
      this.renumber()
    }
    if (event.clientY < 70) window.scrollBy(0, -40)
    else if (event.clientY > window.innerHeight - 70) window.scrollBy(0, 40)
  }

  end(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    const before = this.drag.before
    this.finishDrag()
    if (this.ids().join() !== before.join()) this.save(before)
  }

  cancel() {
    if (!this.drag) return
    this.restore(this.drag.before)
    this.finishDrag()
  }

  finishDrag() {
    this.drag?.item.classList.remove("ring-2", "ring-indigo-500")
    this.drag = null
  }

  up(event) { this.step(event, -1) }
  down(event) { this.step(event, 1) }

  step(event, direction) {
    if (this.busy || !this.editableValue) return
    const item = event.currentTarget.closest('[data-team-order-target="item"]')
    const before = this.ids()
    const index = this.itemTargets.indexOf(item)
    const target = index + direction
    if (target < 0 || target >= before.length) return
    const ids = [...before]
    ids.splice(target, 0, ids.splice(index, 1)[0])
    this.restore(ids)
    this.save(before)
  }

  restore(ids) {
    const byId = new Map(this.itemTargets.map(item => [Number(item.dataset.bookingId), item]))
    ids.forEach(id => { if (byId.has(id)) this.listTarget.appendChild(byId.get(id)) })
    this.renumber()
  }

  setBusy(value) {
    this.busy = value
    this.listTarget.setAttribute("aria-busy", String(value))
    this.element.querySelectorAll("button").forEach(button => { button.disabled = value || !this.editableValue })
  }

  async save(before) {
    this.setBusy(true)
    this.statusTarget.textContent = "Saving team order…"
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ booking_ids: this.ids(), revision: this.revisionValue })
      })
      const payload = await response.json()
      if (!response.ok || !payload.success) throw new Error(payload.message || "Could not save the team order.")
      this.restore(payload.data.players.map(player => player.booking_id))
      this.revisionValue = payload.data.revision
      this.editableValue = payload.data.editable
      this.statusTarget.textContent = "Team order saved."
    } catch (error) {
      this.restore(before)
      this.statusTarget.textContent = `${error.message} Reload the team list before trying again.`
      // Keep a stale roster from being submitted again. Reload gets new arrivals,
      // withdrawals, permissions, and any other administrator's current order.
      this.editableValue = false
      const reload = document.createElement("a")
      reload.href = this.urlValue
      reload.className = "ml-2 underline font-semibold"
      reload.textContent = "Reload team list"
      this.statusTarget.appendChild(reload)
    } finally {
      this.setBusy(false)
    }
  }
}
